import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SortAltIcon } from 'primereact/icons/sortalt'
import { SortAmountDownIcon } from 'primereact/icons/sortamountdown'
import { SortAmountUpAltIcon } from 'primereact/icons/sortamountupalt'
import { UserMetrics } from '../../../types/metrics'
import { exportUserMetricsCSV } from '../../../services/MetricsDashboardService'
import { createPathWithLang } from '../../../utils/languagePathUtils'
import { NetworkConstants } from '../../../utils/NetworkConstants'
import AppTooltip from '../../atoms/AppTooltip'
import DashboardCardToolbar from './DashboardCardToolbar'
import TablePagination from './TablePagination'

type Props = {
  rows: UserMetrics[]
  totalElements: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  loading: boolean
  search: string
  onSearchChange: (value: string) => void
}

type SortKey = keyof Pick<
  UserMetrics,
  | 'name'
  | 'email'
  | 'hasLoggedIn'
  | 'totalReviewsAssigned'
  | 'reviewsCompleted'
  | 'reviewsInProgress'
  | 'reviewsNotStarted'
  | 'reviewsSkipped'
>

type Column = { key: SortKey; label: string }

const COLUMNS: Column[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'hasLoggedIn', label: 'Logged In' },
  { key: 'totalReviewsAssigned', label: 'Total' },
  { key: 'reviewsCompleted', label: 'Done' },
  { key: 'reviewsInProgress', label: 'In Progress' },
  { key: 'reviewsNotStarted', label: 'Not Started' },
  { key: 'reviewsSkipped', label: 'Skipped' },
]

const TALENT_COLUMNS = ['10X', '9X', 'Core', 'Mismatch'] as const

// Total column count: 8 standard + 4 talent rank + 1 reports = 13
const TOTAL_COLUMNS = COLUMNS.length + TALENT_COLUMNS.length + 1

const completedReviewsHref = (reviewerId: number): string =>
  `${createPathWithLang(NetworkConstants.URL_ADMIN_COMPLETED_REVIEWS)}?reviewerId=${reviewerId}`

const skippedReviewsHref = (reviewerId: number): string =>
  `${createPathWithLang(NetworkConstants.URL_ADMIN_SKIPPED_REVIEWS)}?reviewerId=${reviewerId}`

type SortIconProps = { col: SortKey; sortKey: SortKey; sortAsc: boolean }

const SortIcon = ({ col, sortKey, sortAsc }: SortIconProps): JSX.Element => {
  if (sortKey !== col) {
    return <SortAltIcon className="ml-2" style={{ color: '#6c757d' }} aria-hidden="true" />
  }
  const ActiveIcon = sortAsc ? SortAmountUpAltIcon : SortAmountDownIcon
  return <ActiveIcon className="ml-2" style={{ color: '#2196F3' }} aria-hidden="true" />
}

const SEARCH_ID = 'user-metrics-search'

const UserMetricsTable = ({
  rows,
  totalElements,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading,
  search,
  onSearchChange,
}: Props): JSX.Element => {
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortAsc, setSortAsc] = useState(true)

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortAsc(prev => !prev)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  const sortedRows = [...rows].sort((a, b) => {
    const aVal = a[sortKey]
    const bVal = b[sortKey]
    if (aVal === bVal) return 0
    const cmp = aVal < bVal ? -1 : 1
    return sortAsc ? cmp : -cmp
  })

  const ariaSortFor = (col: SortKey): 'ascending' | 'descending' | 'none' => {
    if (sortKey !== col) return 'none'
    return sortAsc ? 'ascending' : 'descending'
  }

  return (
    <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
      <DashboardCardToolbar
        title="Reviewer breakdown"
        tooltipText="Per-reviewer activity: review counts by status and the talent ranks of their completed reviews."
        search={{
          id: SEARCH_ID,
          value: search,
          onChange: onSearchChange,
          placeholder: 'Search by name or email…',
          ariaLabel: 'Search reviewers by name or email',
          className: 'w-96',
        }}
        onExport={exportUserMetricsCSV}
      />

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xl" aria-label="Reviewer metrics">
          <caption className="sr-only">
            Per-reviewer breakdown showing review counts and talent rank distributions
          </caption>
          <thead>
            <tr className="border-b border-soft-gray">
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSortFor(col.key)}
                  className="cursor-pointer select-none whitespace-nowrap py-3 pr-4 text-left text-xl font-semibold text-stone-gray hover:text-dark-blue"
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  <SortIcon col={col.key} sortKey={sortKey} sortAsc={sortAsc} />
                </th>
              ))}
              {TALENT_COLUMNS.map(label => (
                <th key={label} scope="col" className="py-3 pr-4 text-left text-xl font-semibold text-stone-gray">
                  {label}
                </th>
              ))}
              <th scope="col" className="py-3 text-left text-xl font-semibold text-stone-gray">
                Reports
              </th>
            </tr>
          </thead>
          <tbody
            aria-live="polite"
            aria-busy={loading}
            className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}
          >
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={TOTAL_COLUMNS} className="py-12 text-center text-stone-gray">
                  {loading ? 'Loading…' : 'No results'}
                </td>
              </tr>
            ) : (
              sortedRows.map(row => (
                <tr key={row.id} className="odd:bg-gray-cell even:bg-gray-cell/50 hover:bg-just-gray">
                  <td className="py-4 pr-4 font-medium text-blue-gray">{row.name}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.email}</td>
                  <td className="py-4 pr-4">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xl font-medium ${
                        row.hasLoggedIn ? 'bg-green-cell' : 'bg-light-red text-error-red'
                      }`}
                      style={row.hasLoggedIn ? { color: '#15803D' } : undefined}
                    >
                      {row.hasLoggedIn ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="py-4 pr-4 text-blue-gray">{row.totalReviewsAssigned}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.reviewsCompleted}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.reviewsInProgress}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.reviewsNotStarted}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.reviewsSkipped}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.completedByTalentRank.tenX}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.completedByTalentRank.nineX}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.completedByTalentRank.core}</td>
                  <td className="py-4 pr-4 text-blue-gray">{row.completedByTalentRank.mismatch}</td>
                  <td className="py-4">
                    <div className="inline-flex items-center gap-3">
                      {row.reviewsCompleted > 0 ? (
                        <AppTooltip
                          title={
                            <span style={{ display: 'block', fontSize: '14px', lineHeight: 1.45 }}>
                              View this reviewer&apos;s completed reports
                            </span>
                          }
                          placement="top"
                          arrow
                        >
                          <Link
                            to={completedReviewsHref(row.id)}
                            aria-label={`Open reports for ${row.name}`}
                            className="inline-flex items-center text-dark-blue hover:underline"
                          >
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </Link>
                        </AppTooltip>
                      ) : (
                        <span className="text-stone-gray" aria-label="No completed reports">
                          —
                        </span>
                      )}
                      {row.reviewsSkipped > 0 && (
                        <AppTooltip
                          title={
                            <span style={{ display: 'block', fontSize: '14px', lineHeight: 1.45 }}>
                              View this reviewer&apos;s skipped reviews
                            </span>
                          }
                          placement="top"
                          arrow
                        >
                          <Link
                            to={skippedReviewsHref(row.id)}
                            aria-label={`Open skipped reviews for ${row.name}`}
                            className="inline-flex items-center text-error-red hover:underline"
                          >
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                              />
                            </svg>
                          </Link>
                        </AppTooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalElements={totalElements}
        navAriaLabel="Reviewer table pagination"
        textSizeClass="text-xl"
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  )
}

export default UserMetricsTable
